import {DownloadOutlined, PrinterOutlined} from '@ant-design/icons';
import {Button, Modal} from 'antd';
import dayjs from 'dayjs';
import html2pdf from 'html2pdf.js';
import PropTypes from 'prop-types';
import {useRef, useState} from 'react';
import toast from 'react-hot-toast';

const COMPANY = {
  name:    'SAMADHAN INDUSTRIES',
  address: 'Plot No.- F-1, MIDC, Islampur.',
  city:    'Tal.- Walwa, Dist.- Sangli',
  email:   'samadhanindustriesgroup89@gmail.com',
  phone:   '8412912712 / 8485824712',
};

// ─── Wages Statement Print Template ──────────────────────────────────────────

export const WagesStatementTemplate = ({printRef, tableData, totals, monthLabel, selectedYear}) => {
  return (
    <div
      ref={printRef}
      style={{
        fontFamily: '\'Times New Roman\', Times, serif',
        fontSize:   '11px',
        color:      '#000',
        background: '#fff',
        padding:    '8mm 10mm',
        width:      '100%',
        boxSizing:  'border-box',
      }}
    >
      {/* ── Company Header ── */}
      <div style={{textAlign: 'center', marginBottom: '6px'}}>
        <div style={{fontSize: '16px', fontWeight: 'bold', letterSpacing: '2px', textTransform: 'uppercase'}}>
          {COMPANY.name}
        </div>
        <div style={{fontSize: '11px', marginTop: '2px'}}>{COMPANY.address}</div>
        <div style={{fontSize: '11px'}}>{COMPANY.city}</div>
        <div style={{fontSize: '13px', fontWeight: 'bold', marginTop: '6px', textDecoration: 'underline', letterSpacing: '1px'}}>
          WAGES STATEMENT MONTH OF {monthLabel?.toUpperCase()} - {selectedYear}
        </div>
      </div>

      {/* ── Wages Table ── */}
      <table
        style={{
          width:           '100%',
          borderCollapse:  'collapse',
          tableLayout:     'fixed',
          border:          '1px solid black',
          marginTop:       '8px',
        }}
      >
        <colgroup>
          <col style={{width: '4%'}} />
          <col style={{width: '18%'}} />
          <col style={{width: '8%'}} />
          <col style={{width: '6%'}} />
          <col style={{width: '6%'}} />
          <col style={{width: '6%'}} />
          <col style={{width: '6%'}} />
          <col style={{width: '6%'}} />
          <col style={{width: '9%'}} />
          <col style={{width: '9%'}} />
          <col style={{width: '9%'}} />
          <col style={{width: '7%'}} />
          <col style={{width: '9%'}} />
          <col style={{width: '7%'}} />
        </colgroup>

        <thead>
          {/* Grouped header row */}
          <tr style={{backgroundColor: '#e9edf4'}}>
            <th rowSpan={2} style={thStyle}>SR.<br />NO</th>
            <th rowSpan={2} style={thStyle}>NAME OF<br />WORKER</th>
            <th rowSpan={2} style={thStyle}>PRESENTY</th>
            {/* OT group */}
            <th colSpan={2} style={thStyle}>O.T.<br />(DAYS)</th>
            <th colSpan={2} style={thStyle}>O.T.<br />(HRS.)</th>
            <th colSpan={2} style={thStyle}>O.T.<br />AMT.</th>
            <th rowSpan={2} style={thStyle}>PER DAY<br />WAGES</th>
            <th rowSpan={2} style={thStyle}>WAGES<br />RATE</th>
            <th rowSpan={2} style={thStyle}>WAGES</th>
            <th rowSpan={2} style={thStyle}>(-) ADV.</th>
            <th rowSpan={2} style={thStyle}>NET WAGES<br />PAYABLE</th>
          </tr>
          <tr style={{backgroundColor: '#e9edf4'}}>
            <th style={thStyle}>DAYS</th>
            <th style={thStyle}>HRS.</th>
            <th style={thStyle}>DAYS</th>
            <th style={thStyle}>HRS.</th>
            <th style={thStyle}>AMT.</th>
            <th style={thStyle}>Bonus</th>
          </tr>
        </thead>

        <tbody>
          {tableData.map((row, idx) => (
            <tr key={row.key} style={{height: '26px'}}>
              {td(idx + 1, {textAlign: 'center'})}
              {td(row.name, {textAlign: 'left', paddingLeft: '4px', fontWeight: 'bold', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis'})}
              {td(row.presentDays, {textAlign: 'center'})}
              {/* OT columns — zeroed since data not tracked */}
              {td(row.attendanceOvertime > 0 ? row.attendanceOvertime : 0, {textAlign: 'center'})}
              {td(0, {textAlign: 'center'})}
              {td(0, {textAlign: 'center'})}
              {td(0, {textAlign: 'center'})}
              {td(0, {textAlign: 'center'})}
              {td(0, {textAlign: 'center'})}
              {td(`₹ ${(row.perDayWages || 0).toLocaleString('en-IN')}`, {textAlign: 'right'})}
              {td(`₹ ${(row.totalWagesRate || 0).toLocaleString('en-IN')}`, {textAlign: 'right'})}
              {td(
                <span style={{fontWeight: 'bold', color: '#1d4ed8'}}>
                  {(row.wages || 0).toLocaleString('en-IN')}
                </span>,
                {textAlign: 'right'},
              )}
              {td(
                row.advanceDeducted > 0
                  ? <span style={{color: '#dc2626', fontWeight: 'bold'}}>{row.advanceDeducted.toLocaleString('en-IN')}</span>
                  : <span style={{color: '#9ca3af'}}>—</span>,
                {textAlign: 'right'},
              )}
              {td(
                <span style={{fontWeight: 'bold', color: row.netPayable < 0 ? '#dc2626' : '#15803d'}}>
                  {(row.netPayable || 0).toLocaleString('en-IN')}
                </span>,
                {textAlign: 'right'},
              )}
            </tr>
          ))}

          {/* ── Totals Row ── */}
          <tr style={{backgroundColor: '#e9edf4', height: '28px'}}>
            <td colSpan={2} style={{...tdBase, textAlign: 'center', fontWeight: 'bold', fontSize: '11px', letterSpacing: '1px'}}>
              TOTAL
            </td>
            <td style={{...tdBase, textAlign: 'center', fontWeight: 'bold'}}>{totals.presentDays}</td>
            {/* OT cols */}
            <td style={tdBase} />
            <td style={tdBase} />
            <td style={tdBase} />
            <td style={tdBase} />
            <td style={tdBase} />
            <td style={tdBase} />
            <td style={tdBase} />
            <td style={tdBase} />
            <td style={{...tdBase, textAlign: 'right', fontWeight: 'bold', color: '#1d4ed8'}}>
              ₹ {totals.wages.toLocaleString('en-IN')}
            </td>
            <td style={{...tdBase, textAlign: 'right', fontWeight: 'bold', color: '#dc2626'}}>
              {totals.advanceDeducted > 0 ? `₹ ${totals.advanceDeducted.toLocaleString('en-IN')}` : '—'}
            </td>
            <td style={{...tdBase, textAlign: 'right', fontWeight: 'bold', color: totals.netPayable < 0 ? '#dc2626' : '#15803d'}}>
              ₹ {totals.netPayable.toLocaleString('en-IN')}
            </td>
          </tr>
        </tbody>
      </table>

      {/* ── Summary Box ── */}
      <table style={{width: '100%', borderCollapse: 'collapse', marginTop: '12px'}}>
        <tbody>
          <tr>
            {/* Summary cards */}
            <td style={{verticalAlign: 'top', width: '60%', paddingRight: '12px'}}>
              <table style={{width: '100%', borderCollapse: 'collapse', border: '1px solid black'}}>
                <tbody>
                  <tr style={{backgroundColor: '#e9edf4'}}>
                    <td colSpan={2} style={{...tdBase, textAlign: 'center', fontWeight: 'bold', padding: '4px'}}>
                      SUMMARY
                    </td>
                  </tr>
                  {[
                    ['Total Employees', tableData.length],
                    ['Total Present Days', `${totals.presentDays} days`],
                    ['Gross Wages', `₹ ${totals.wages.toLocaleString('en-IN')}`],
                    ['Advance Deducted', `₹ ${totals.advanceDeducted.toLocaleString('en-IN')}`],
                    ['Net Payable', `₹ ${totals.netPayable.toLocaleString('en-IN')}`],
                  ].map(([label, value]) => (
                    <tr key={label}>
                      <td style={{...tdBase, padding: '3px 6px', fontWeight: 'bold', width: '55%'}}>{label}</td>
                      <td style={{...tdBase, padding: '3px 6px', textAlign: 'right', borderLeft: '1px solid black'}}>{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </td>

            {/* Signatures */}
            <td style={{verticalAlign: 'bottom', width: '40%'}}>
              <div style={{textAlign: 'right', marginTop: '40px'}}>
                <p style={{margin: 0, fontWeight: 'bold', fontSize: '11px'}}>
                  For, {COMPANY.name}
                </p>
                <p style={{margin: '40px 0 0 0', fontStyle: 'italic', fontSize: '10px'}}>
                  PROPRIETOR / AUTHORISED SIGNATORY
                </p>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

WagesStatementTemplate.propTypes = {
  printRef:     PropTypes.object.isRequired,
  tableData:    PropTypes.array.isRequired,
  totals:       PropTypes.object.isRequired,
  monthLabel:   PropTypes.string.isRequired,
  selectedYear: PropTypes.number.isRequired,
};

// ─── Shared cell styles ───────────────────────────────────────────────────────

const thStyle = {
  border:          '1px solid black',
  padding:         '4px 2px',
  textAlign:       'center',
  verticalAlign:   'middle',
  fontWeight:      'bold',
  fontSize:        '10px',
  backgroundColor: '#e9edf4',
  whiteSpace:      'pre-line',
  fontFamily:      '\'Times New Roman\', Times, serif',
};

const tdBase = {
  border:        '1px solid black',
  height:        '26px',
  padding:       '0 4px',
  verticalAlign: 'middle',
  fontSize:      '11px',
  fontFamily:    '\'Times New Roman\', Times, serif',
  overflow:      'hidden',
  whiteSpace:    'nowrap',
};

const td = (content, extraStyle = {}) => (
  <td style={{...tdBase, ...extraStyle}}>{content}</td>
);

// ─── Modal Wrapper ────────────────────────────────────────────────────────────

const GenerateWagesPdf = ({open, onClose, tableData, totals, monthLabel, selectedYear}) => {
  const printRef                      = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const filename = `Wages_Statement_${monthLabel}_${selectedYear}.pdf`;

  const handleDownload = async () => {
    if (!printRef.current) {return;}
    setDownloading(true);
    try {
      await html2pdf()
        .set({
          margin:      [5, 5, 5, 5],
          filename,
          image:       {type: 'jpeg', quality: 0.98},
          html2canvas: {
            scale:   2,
            useCORS: true,
            logging: false,
            onclone: doc => {
              doc.querySelectorAll('td, th').forEach(el => {
                if (el.style.verticalAlign !== 'top') {
                  el.style.verticalAlign = 'middle';
                }
                el.style.lineHeight = '1.2';
              });
            },
          },
          jsPDF: {unit: 'mm', format: 'a4', orientation: 'landscape'},
        })
        .from(printRef.current)
        .save();
      toast.success('Wages statement PDF downloaded!');
      onClose();
    } catch {
      toast.error('PDF generation failed');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      destroyOnClose
      width={1200}
      title={(
        <div className={'flex items-center gap-2'}>
          <PrinterOutlined className={'text-blue-600'} />
          <span className={'font-bold'}>
            Wages Statement Preview —{' '}
            <span className={'font-mono text-blue-600'}>{monthLabel} {selectedYear}</span>
          </span>
        </div>
      )}
      styles={{body: {maxHeight: '80vh', overflowY: 'auto', padding: '16px 20px'}}}
      footer={(
        <div className={'flex justify-end gap-3 py-1.5'}>
          <Button onClick={onClose}>Close</Button>
          <Button
            type={'primary'}
            icon={<DownloadOutlined />}
            loading={downloading}
            onClick={handleDownload}
          >
            Download PDF
          </Button>
        </div>
      )}
    >
      <div className={'border border-slate-200 rounded-lg overflow-hidden shadow-sm'}>
        <WagesStatementTemplate
          printRef={printRef}
          tableData={tableData}
          totals={totals}
          monthLabel={monthLabel}
          selectedYear={selectedYear}
        />
      </div>
    </Modal>
  );
};

GenerateWagesPdf.propTypes = {
  open:         PropTypes.bool.isRequired,
  onClose:      PropTypes.func.isRequired,
  tableData:    PropTypes.array.isRequired,
  totals:       PropTypes.object.isRequired,
  monthLabel:   PropTypes.string.isRequired,
  selectedYear: PropTypes.number.isRequired,
};

export default GenerateWagesPdf;
